// The shared story's pickup orders (outlet -> recycler), as the Zero API lists them.
// Dashboards, branch metrics and the per-material daily split all derive from this
// one array, so their totals reconcile.

import {
  DEMO_OUTLET_ORG_ID,
  DEMO_RECYCLER_ORG_ID,
  generatePickupOrders,
  materialById,
  paginate,
  type DemoPickupOrder,
} from '../core';
import type { PickupOrderListItem } from '../../types/pickupOrder.type';
import { materialCategory, naiveRequired, toMs } from './helpers';

let orders: DemoPickupOrder[] = generatePickupOrders();

export function resetPickupOrders(): void {
  orders = generatePickupOrders();
}

/** Every story order, oldest first. */
export function allPickupOrders(): DemoPickupOrder[] {
  return orders;
}

/** kg the dashboards count for an order: the verified weight once weighed, else the estimate. */
export function orderKg(order: DemoPickupOrder): number {
  return order.verified_kg ?? order.estimated_kg;
}

/** Orders an organisation sees: the outlet raised them, the recycler receives them. */
export function ordersForOrg(orgId: string | null | undefined): DemoPickupOrder[] {
  if (orgId === DEMO_OUTLET_ORG_ID) return orders.filter((o) => o.outlet_org_id === orgId);
  if (orgId === DEMO_RECYCLER_ORG_ID) return orders.filter((o) => o.recycler_org_id === orgId);
  return [];
}

/** Orders of the organisation created in month `key` ('YYYY-MM'); null = whole history. */
export function ordersInMonth(orgId: string | null | undefined, key: string | null): DemoPickupOrder[] {
  const list = ordersForOrg(orgId);
  return key ? list.filter((o) => o.created_at.startsWith(key)) : list;
}

export function toPickupOrderListItem(order: DemoPickupOrder): PickupOrderListItem {
  const material = materialById(order.material_id);
  const category = materialCategory(order.material_id);
  return {
    id: order.id,
    amount: orderKg(order),
    amount_unit: 'kg',
    status: order.status,
    material_type_id: order.material_id,
    material_name_snapshot: material?.name_en ?? null,
    category_name_snapshot: category,
    collection_site_id: order.site_id,
    created_at: naiveRequired(order.created_at),
    material_type: material ? { id: material.id, name: material.name_en, parent_category: category } : null,
  };
}

export interface PickupOrderQuery {
  organizationId: string | null;
  start?: string;
  end?: string;
  status?: string;
  page?: unknown;
  limit?: unknown;
  sortBy?: string;
  sortDirection?: string;
}

/** GET /pickup_order: filtered by creation time (naive UTC bounds), sorted, paged. */
export function listPickupOrders(q: PickupOrderQuery): { pickup_orders: PickupOrderListItem[]; total: number; page: number; limit: number } {
  const startMs = toMs(q.start);
  const endMs = toMs(q.end);
  let list = ordersForOrg(q.organizationId).filter((o) => {
    const t = Date.parse(o.created_at);
    if (startMs !== null && t < startMs) return false;
    if (endMs !== null && t > endMs) return false;
    if (q.status && o.status !== q.status) return false;
    return true;
  });
  const desc = (q.sortDirection ?? '').toLowerCase() === 'desc';
  const key = q.sortBy === 'created_at' ? 'created_at' : 'id';
  list = [...list].sort((a, b) => (desc ? -1 : 1) * a[key].localeCompare(b[key]));
  const page = paginate(list.map(toPickupOrderListItem), q.page, q.limit);
  return { pickup_orders: page.items, total: page.total, page: page.page, limit: page.limit };
}
